import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Dropdown, Form } from 'react-bootstrap';

import { UseOutletContextContact } from './Contact';
import { StateLanguage } from '../../../languages/config/StateLanguage';
import { ApiDigitalAdminContact } from '../../../services/admin/contact.service';
import { Error } from '../../../models/error.model';
import { Message } from '../../../models/message.model';
import { Company } from '../../../models/company.model';
import { Contact, ContactsData } from '../../../models/contact.model';
import { Container } from '../../../styles/container.style';
import { GlobalSwal } from '../../../styles/swal.style';
import { modalShow } from '../../../utils/modal.util';
import { contactTypeList, contactTypeValue } from '../../../libraries/contact.type.library';
import { countryName } from '../../../libraries/country.library';
import { removeTooltip, uploadTooltip } from '../../../utils/tooltip.util';
import { sortValue } from '../../../libraries/sort.library';
import { getError, getMessage } from '../../../languages/translations/response';
import { formatContact, formatLocation, normalizeContacts, normalizeContactData } from '../../../scripts/format.value.script';
import { errorWebHandler } from '../../../scripts/error.web.handler.script';

import { isChatAllowed } from '../../../scripts/chat.auth';
import { openChat } from '../../../scripts/chat.controller';

import AppModalContactExport from '../../../components/modal/ModalContactExport';
import AppModalContactUpdate from '../../../components/modal/ModalContactUpdate';
import AppPagination from '../../../components/element/Pagination';
import AppStorageRemove from '../../../components/element/StorageRemove';
import ContactAvatar from '../../../components/element/ContactAvatar';
import AppPreloader from '../../../components/Preloader';
import AppNone from '../../../components/element/None';

export interface ContactListPageProps {};

let errorResponse: Error, messageResponse: Message, contactsResponse: ContactsData, contactResponse: Contact;

const ContactListPage: React.FunctionComponent<ContactListPageProps> = props => {
  const {setRoute, companyForUser, meCompanies, allContacts, countriesOfContacts, loadAllContacts, loadContactsForCompany} = UseOutletContextContact()
  const {lang} = StateLanguage()

  const [mounted, setMounted] = useState(false)
  const [button, setButton] = useState(false)
  const [filter, setFilter] = useState({search: '', options: {country: '', type: '', sort: ''}})
  const [pagination, setPagination] = useState({current: 1, limit: 10, total: 0, pages: [1]})
  const [contacts, setContacts] = useState<Contact[] | null>(null)
  const [checkedContacts, setCheckedContacts] = useState({count: 0, list: [] as string[]})
  const [contactCompanies, setContactCompanies] = useState<Company[] | null>(null)
  const [searchText, setSearchText] = useState('')
  const [contact, setContact] = useState<Contact | null>(null)
  const [storage, setStorage] = useState<any>(null)

  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const searchAbortRef = useRef<AbortController | null>(null)

  const loadContacts = async (id_company: string, page: number, callback: any, signal?: AbortSignal) => {
    await ApiDigitalAdminContact.getContacts(id_company, filter.search, filter.options.country, filter.options.type, filter.options.sort, page, pagination.limit, signal).then( (response) => {
      if (response.status === 0) return // cancelled
      if (response.status === 200) {
        contactsResponse = response.data

        localStorage.setItem('filter_contacts', JSON.stringify({
          company_id: id_company,
          country: filter.options.country,
          type: filter.options.type,
          sort: filter.options.sort
        }))

        let pagesOfPagination: number[] = []
        for (let i = 1; i <= (contactsResponse.pagination.pages); i++) {
          pagesOfPagination.push(i)
        }

        setContacts(normalizeContacts(contactsResponse.items))
        setCheckedContacts({count: 0, list: []})
        setButton(Boolean(filter.options.country || filter.options.type || filter.options.sort))
        setPagination({...pagination, current: contactsResponse.pagination.page, total: contactsResponse.pagination.total, pages: pagesOfPagination})
        setStorage((localStorage.getItem('filter_contacts')) ? JSON.parse(localStorage.getItem('filter_contacts')!) : null)
        callback()
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const submitContactUpdate = async (item: string) => {
    if (!companyForUser || !meCompanies) return null

    removeTooltip()
    modalShow('modal-contact-update')

    await ApiDigitalAdminContact.getContact(companyForUser.id, item).then( (response) => {
      if (response.status === 200) {
        contactResponse = response.data

        setContact(normalizeContactData(contactResponse))
        setContactCompanies(meCompanies.filter((company) => (contactResponse.data.companies.includes(company.id))))
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const submitContactDelete = async (item: string) => {
    if (!companyForUser) return null

    removeTooltip()

    GlobalSwal.fire({
      title: lang.labels.youWantToDeleteThisContact,
      text: lang.labels.youWillNotBeAbleToUndo,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.yesDelete,
      cancelButtonText: lang.labels.noCancel
    }).then(async (result) => {
      if (result.isConfirmed) {
        await ApiDigitalAdminContact.deleteContacts(companyForUser.id, [item]).then( (response) => {
          if (response.status === 200) {
            messageResponse = response.data

            loadContacts(companyForUser.id, 1, () => {
              loadAllContacts(companyForUser.id)
              loadContactsForCompany(companyForUser.id)

              GlobalSwal.fire({
                title: getMessage(messageResponse.message, lang.code),
                text: lang.labels.actionCompletedReturningToPage,
                icon: 'success',
                showConfirmButton: false,
                timer: 2200
              })
            })
          } else {
            errorResponse = response.data

            GlobalSwal.fire({
              title: getError(errorResponse.code, lang.code, errorResponse.message),
              text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
              icon: 'error',
              confirmButtonText: lang.labels.okGotIt
            })
          }
        }).catch( (error) => {
          errorWebHandler(lang, error)
        })
      }
    })
  }

  const submitContactDeleteList = async () => {
    if (!companyForUser) return null

    GlobalSwal.fire({
      title: lang.labels.youWantToDeleteSelectedContacts,
      text: lang.labels.youWillNotBeAbleToUndo,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.yesDelete,
      cancelButtonText: lang.labels.noCancel
    }).then(async (result) => {
      if (result.isConfirmed) {
        await ApiDigitalAdminContact.deleteContacts(companyForUser.id, checkedContacts.list).then( (response) => {
          if (response.status === 200) {
            messageResponse = response.data

            loadContacts(companyForUser.id, 1, () => {
              loadAllContacts(companyForUser.id)
              loadContactsForCompany(companyForUser.id)

              GlobalSwal.fire({
                title: getMessage(messageResponse.message, lang.code),
                text: lang.labels.actionCompletedReturningToPage,
                icon: 'success',
                showConfirmButton: false,
                timer: 2200
              })
            })
          } else {
            errorResponse = response.data

            GlobalSwal.fire({
              title: getError(errorResponse.code, lang.code, errorResponse.message),
              text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
              icon: 'error',
              confirmButtonText: lang.labels.okGotIt
            })
          }
        }).catch( (error) => {
          errorWebHandler(lang, error)
        })
      }
    })
  }

  const handleChangeSelectAllContacts = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    let list: string[] = (event.target.checked) ? event.target.value.split(',') : []
    setCheckedContacts({count: list.length, list: list})
  }

  const handleChangeSelectContact = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    let list: string[] = (event.target.checked) ? (!checkedContacts.list.includes(event.target.value)) ? checkedContacts.list.concat(event.target.value) : checkedContacts.list : checkedContacts.list.filter((item) => (item !== event.target.value))
    setCheckedContacts({count: list.length, list: list})
  }

  const handleChangeSort = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setFilter({...filter, options: {...filter.options, sort: event.target.value}})
  }

  const handleChangeCountry = (event: React.ChangeEvent <HTMLFormElement | HTMLSelectElement>) => {
    setFilter({...filter, options: {...filter.options, country: event.target.value,}})
  }

  const handleChangeType = (event: React.ChangeEvent <HTMLFormElement | HTMLSelectElement>) => {
    setFilter({...filter, options: {...filter.options, type: event.target.value}})
  }

  const handleChangeSearch = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    if (!companyForUser) return null

    const value = event.target.value
    setSearchText(value)

    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
    if (searchAbortRef.current) searchAbortRef.current.abort()

    searchTimerRef.current = setTimeout(() => {
      const abort = new AbortController()
      searchAbortRef.current = abort
      filter.search = value
      filter.options = {country: '', type: '', sort: ''}
      loadContacts(companyForUser.id, 1, () => {}, abort.signal)
    }, 300)
  }

  const executeShowModalContactExport = async () => {
    modalShow('modal-contact-export')
  }

  const executeFilterApply = () => {
    if (!companyForUser) return null

    filter.search = ''
    setSearchText('')
    loadContacts(companyForUser.id, 1, () => {})
}

  const executeFilterReset = () => {
    if (!companyForUser) return null

    filter.options = {country: '', type: '', sort: ''}
    loadContacts(companyForUser.id, 1, () => {})
  }

  const executeFilterRemove = (item: string) => {
    if (!companyForUser) return null

    if ((filter.options.country || filter.options.type || filter.options.sort).length !== 0) {
      for (let key of Object.keys(filter.options) as Array<keyof typeof filter.options>) {
        if (key === item) {
          filter.options[key] = ''
        }
      }
      loadContacts(companyForUser.id, 1, () => {})
    }
  }


  function loadPagination(current: number) {
    if (!companyForUser) return null
    loadContacts(companyForUser.id, current, () => {})
  }

  useEffect( () => {
    let filter_contacts: any = (localStorage.getItem('filter_contacts')) ? JSON.parse(localStorage.getItem('filter_contacts')!) : null

    if (companyForUser) {
      filter.search = ''
      setSearchText('')
      filter.options.country = (filter_contacts && filter_contacts.company_id && companyForUser.id) ? filter_contacts.country : ''
      filter.options.type = (filter_contacts && filter_contacts.company_id && companyForUser.id) ? filter_contacts.type : ''
      filter.options.sort = (filter_contacts && filter_contacts.company_id && companyForUser.id) ? filter_contacts.sort : ''
      checkedContacts.count = 0
      checkedContacts.list = []
      pagination.current = 0
      pagination.total = 0

      setContacts(null)
      setButton(false)
      loadContacts(companyForUser.id, 1, () => { uploadTooltip() })
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.contacts, branch: lang.labels.listContacts}, company: true})

    return () => {
      setMounted(false)
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
      if (searchAbortRef.current) searchAbortRef.current.abort()
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { countriesOfContacts && contacts
        ?
        <div className="card card card-flush">
          <div className="card-header">
            <div className="card-title">
              <div className="d-flex align-items-center position-relative">
                <span className="svg-icon svg-icon-1 position-absolute ms-4">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <rect opacity="0.5" x="17.0365" y="15.1223" width="8.15546" height="2" rx="1" transform="rotate(45 17.0365 15.1223)" fill="black" />
                    <path d="M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z" fill="black" />
                  </svg>
                </span>
                <input className="form-control w-350px ps-14" type="text" autoComplete="off" placeholder={lang.labels.searchContact} value={searchText} onChange={handleChangeSearch} />
              </div>
            </div>
            <div className="card-toolbar">
              <Container property={(checkedContacts.count === 0).toString()}>
                <div className="d-flex justify-content-end align-items-center">
                  <Dropdown>
                    <Dropdown.Toggle variant={`btn ${button ? "btn-primary" : "btn-light-primary"} me-2`}>
                      <div className="d-flex align-items-center">
                        <span className="svg-icon svg-icon-2 ms-0">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M19.0759 3H4.72777C3.95892 3 3.47768 3.83148 3.86067 4.49814L8.56967 12.6949C9.17923 13.7559 9.5 14.9582 9.5 16.1819V19.5072C9.5 20.2189 10.2223 20.7028 10.8805 20.432L13.8805 19.1977C14.2553 19.0435 14.5 18.6783 14.5 18.273V13.8372C14.5 12.8089 14.8171 11.8056 15.408 10.964L19.8943 4.57465C20.3596 3.912 19.8856 3 19.0759 3Z" fill="black" />
                          </svg>
                        </span>
                        {lang.labels.filter}
                      </div>
                    </Dropdown.Toggle>
                    <Dropdown.Menu variant="menu menu-sub menu-sub-dropdown w-300px w-md-325px" align="end">
                      <div className="mt-5 mx-5">
                        <div className="fs-5 text-dark fw-bolder">{lang.labels.filterOptions}</div>
                      </div>
                      <div className="separator border-gray-200"></div>
                      <div className="mt-3 m-5">
                        <div className="mb-3">
                          <label className="form-label fs-7">{lang.labels.sortAlphabetically}:</label>
                          <div className="d-flex row mx-0">
                            <label className="col-6 form-check form-check-sm form-check-custom form-check-solid">
                              <input className="form-check-input" type="radio" value="asc" checked={filter.options.sort === 'asc'} onChange={handleChangeSort} />
                              <span className="form-check-label fw-bold fs-8">{lang.labels.ascending}</span>
                            </label>
                            <label className="col-6 form-check form-check-sm form-check-custom form-check-solid">
                              <input className="form-check-input" type="radio" value="desc" checked={filter.options.sort === 'desc'} onChange={handleChangeSort} />
                              <span className="form-check-label fw-bold fs-8">{lang.labels.descending}</span>
                            </label>
                          </div>
                        </div>
                        <div className="mb-3">
                          <label className="form-label fs-7">{lang.labels.countryContact}:</label>
                          <Form.Select bsPrefix="form-select form-select-sm" aria-placeholder={lang.labels.select} name="country" value={filter.options.country} onChange={handleChangeCountry}>
                            <option value="" className={`${countriesOfContacts.length > 0 && "d-none"}`}>{lang.labels.select}</option>
                            { countriesOfContacts.map (( (item, index) => { return (
                              <option key={index} value={item}>{countryName(lang.code, item)}</option>
                            )}))}
                          </Form.Select>
                        </div>
                        <div className="mb-5">
                          <label className="form-label fs-7">{lang.labels.typeOfContact}:</label>
                          <Form.Select bsPrefix="form-select form-select-sm" aria-placeholder={lang.labels.select} name="type" value={filter.options.type} onChange={handleChangeType}>
                            <option value="" className={`${contacts.length > 0 && "d-none"}`}>{lang.labels.select}</option>
                            { contacts.length > 0 &&
                              <>
                                { contactTypeList(lang).map (( (item, index) => { return (
                                  <option key={index} value={item.code}>{item.display}</option>
                                )}))}
                              </>
                            }
                          </Form.Select>
                        </div>
                        <div className="d-flex justify-content-end">
                          <Dropdown.Item bsPrefix="btn btn-sm btn-light btn-active-light-primary" onClick={executeFilterReset}>{lang.labels.reset}</Dropdown.Item>
                          <Dropdown.Item bsPrefix="btn btn-sm btn-primary ms-3" onClick={executeFilterApply}>{lang.labels.apply}</Dropdown.Item>
                        </div>
                      </div>
                    </Dropdown.Menu>
                  </Dropdown>
                  <button className="btn btn-primary" type="button" onClick={executeShowModalContactExport}>
                    <div className="d-flex align-items-center">
                      <span className="svg-icon svg-icon-2 ms-0">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <rect opacity="0.3" x="12.75" y="4.25" width="12" height="2" rx="1" transform="rotate(90 12.75 4.25)" fill="black" />
                          <path d="M12.0573 6.11875L13.5203 7.87435C13.9121 8.34457 14.6232 8.37683 15.056 7.94401C15.4457 7.5543 15.4641 6.92836 15.0979 6.51643L12.4974 3.59084C12.0996 3.14332 11.4004 3.14332 11.0026 3.59084L8.40206 6.51643C8.0359 6.92836 8.0543 7.5543 8.44401 7.94401C8.87683 8.37683 9.58785 8.34458 9.9797 7.87435L11.4427 6.11875C11.6026 5.92684 11.8974 5.92684 12.0573 6.11875Z" fill="black" />
                          <path d="M18.75 8.25H17.75C17.1977 8.25 16.75 8.69772 16.75 9.25C16.75 9.80228 17.1977 10.25 17.75 10.25C18.3023 10.25 18.75 10.6977 18.75 11.25V18.25C18.75 18.8023 18.3023 19.25 17.75 19.25H5.75C5.19772 19.25 4.75 18.8023 4.75 18.25V11.25C4.75 10.6977 5.19771 10.25 5.75 10.25C6.30229 10.25 6.75 9.80228 6.75 9.25C6.75 8.69772 6.30229 8.25 5.75 8.25H4.75C3.64543 8.25 2.75 9.14543 2.75 10.25V19.25C2.75 20.3546 3.64543 21.25 4.75 21.25H18.75C19.8546 21.25 20.75 20.3546 20.75 19.25V10.25C20.75 9.14543 19.8546 8.25 18.75 8.25Z" fill="#C4C4C4" />
                        </svg>
                      </span>
                      {lang.labels.export}
                    </div>
                  </button>
                  <Link to="/main/contact/create" className="btn btn-primary ms-2" title={lang.labels.createContact}>
                    <div className="d-flex align-items-center">
                      <span className="svg-icon svg-icon-2 ms-0">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <rect opacity="0.3" x="2" y="2" width="20" height="20" rx="5" fill="black" />
                          <rect x="10.8891" y="17.8033" width="12" height="2" rx="1" transform="rotate(-90 10.8891 17.8033)" fill="black" />
                          <rect x="6.01041" y="10.9247" width="12" height="2" rx="1" fill="black" />
                        </svg>
                      </span>
                    </div>
                  </Link>
                </div>
              </Container>
              <Container property={(checkedContacts.count > 0).toString()}>
                <div className="d-flex justify-content-end align-items-center">
                  <div className="fw-bolder me-5">
                    <span className="text-lowercase me-2">{checkedContacts.count}</span>
                    {lang.labels.selected}
                  </div>
                  <button className="btn btn-danger" type="button" onClick={submitContactDeleteList}>{lang.labels.deleteSelected}</button>
                </div>
              </Container>
            </div>
          </div>
          { storage && button &&
            <div className="card-scroll d-flex bg-light-primary rounded border-0 p-3 mx-7 mb-4">
              <button className={`btn btn-sm btn-info btn-color-dark text-nowrap fs-8 px-2 py-1 mx-1 ${storage.country.length === 0 && "d-none"}`} type="button" onClick={() => executeFilterRemove("country")}>
                <AppStorageRemove attribute={lang.labels.country} description={countryName(lang.code, storage.country)}></AppStorageRemove>
              </button>
              <button className={`btn btn-sm btn-info btn-color-dark text-nowrap fs-8 px-2 py-1 mx-1 ${storage.type.length === 0 && "d-none"}`} type="button" onClick={() => executeFilterRemove("type")}>
                <AppStorageRemove attribute={lang.labels.type} description={contactTypeValue(lang, storage.type)}></AppStorageRemove>
              </button>
              <button className={`btn btn-sm btn-info btn-color-dark text-nowrap fs-8 px-2 py-1 mx-1 ${storage.sort.length === 0 && "d-none"}`} type="button" onClick={() => executeFilterRemove("sort")}>
                <AppStorageRemove attribute={lang.labels.sortAlphabetically} description={sortValue(lang, storage.sort)}></AppStorageRemove>
              </button>
            </div>
          }
          <div className="card-body pt-0">
            <div className="dataTables_wrapper">
              <div className="table-responsive">
                <table className="table align-middle table-row-dashed mb-2">
                  <thead className="border-bottom">
                    <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                      <th className="w-25px sorting_disabled">
                        <div className="form-check form-check-sm form-check-custom form-check-solid">
                          <input className="form-check-input" type="checkbox" value={contacts.map(item => item.id)} checked={contacts.length === checkedContacts.count && checkedContacts.count > 0} onChange={handleChangeSelectAllContacts} />
                        </div>
                      </th>
                      <th className="min-w-300px sorting">{lang.labels.user}</th>
                      <th className="min-w-100px sorting">{lang.labels.type}</th>
                      <th className="min-w-100px sorting">{lang.labels.contactCode}</th>
                      <th className="min-w-100px sorting">{lang.labels.phone}</th>
                      <th className="min-w-100px sorting">{lang.labels.email}</th>
                      <th className="w-75px sorting_disabled text-end">{lang.labels.actions}</th>
                    </tr>
                  </thead>
                  { contacts.length > 0
                    ?
                    <tbody className="text-secondary fw-bold">
                      { contacts.map (( (item, index) => { return (
                        <tr key={index} className="row-dashed">
                          <td>
                            <div className="form-check form-check-sm form-check-custom form-check-solid">
                              <input className="form-check-input" type="checkbox" value={item.id} checked={checkedContacts.list.includes(item.id)} onChange={handleChangeSelectContact} />
                            </div>
                          </td>
                          <td className="d-flex align-items-center">
                              <div className="symbol symbol-circle symbol-50px overflow-hidden me-5">
                                <Link to={"/main/contact/view/" + item.id}>
                                  <ContactAvatar
                                    type={item.type}
                                    name={item.data.name}
                                    firstName={item.data.first_name}
                                    lastName={item.data.last_name}
                                    logo={item.data.logo}
                                    size={50}
                                  />
                                </Link>
                              </div>
                              <div className="d-flex flex-column">
                                <Link to={"/main/contact/view/" + item.id} className="fw-bolder text-dark text-hover-primary mb-1">{formatContact(item)}</Link>
                                <small>{formatLocation(lang, item.data.address)}</small>
                              </div>
                          </td>
                          <td>
                            <span className="badge badge-light fs-7">{contactTypeValue(lang, item.type)}</span>
                          </td>
                          <td>
                            <AppNone condition={(item.data.contact_code ?? '').length > 0} display={item.data.contact_code}></AppNone>
                          </td>
                          <td>
                            <AppNone condition={item.data.phone.length > 0} display={item.data.phone}></AppNone>
                          </td>
                          <td>
                            <AppNone condition={item.data.email.length > 0} display={item.data.email}></AppNone>
                          </td>
                          <td>
                            <div className="d-flex justify-content-end">
                              {isChatAllowed() && item.is_user && item.data.uuid && (
                                <button
                                  className="btn btn-sm btn-icon btn-light-success me-1"
                                  type="button"
                                  data-bs-toggle="tooltip"
                                  data-bs-trigger="hover"
                                  data-bs-placement="left"
                                  data-bs-original-title={lang.labels.chatStartConversation}
                                  onClick={() => { removeTooltip(); openChat({ contactId: item.id, contactName: formatContact(item), contactType: item.type as 'person' | 'company', contactUuid: item.data.uuid }) }}
                                >
                                  <span className="svg-icon svg-icon-3">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                      <path opacity="0.3" d="M20 3H4C2.89543 3 2 3.89543 2 5V16C2 17.1046 2.89543 18 4 18H4.5C5.05228 18 5.5 18.4477 5.5 19V21.5052C5.5 22.1441 6.21232 22.5253 6.74118 22.1708L11.3753 19.0955C11.5301 18.9912 11.7123 18.9354 11.8989 18.9354H20C21.1046 18.9354 22 18.0399 22 16.9354V5C22 3.89543 21.1046 3 20 3Z" fill="currentColor"/>
                                      <rect x="6" y="12" width="7" height="2" rx="1" fill="currentColor"/>
                                      <rect x="6" y="7" width="12" height="2" rx="1" fill="currentColor"/>
                                    </svg>
                                  </span>
                                </button>
                              )}
                              <Link to={"/main/contact/entry/" + item.id} className="btn btn-sm btn-icon btn-light-primary" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-placement="left" data-bs-original-title={lang.labels.accountStatement} onClick={removeTooltip}>
                                <span className="svg-icon svg-icon-3">
                                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path opacity="0.3" d="M19 22H5C4.4 22 4 21.6 4 21V3C4 2.4 4.4 2 5 2H14L20 8V21C20 21.6 19.6 22 19 22ZM15 17C15 16.4 14.6 16 14 16H8C7.4 16 7 16.4 7 17C7 17.6 7.4 18 8 18H14C14.6 18 15 17.6 15 17ZM17 12C17 11.4 16.6 11 16 11H8C7.4 11 7 11.4 7 12C7 12.6 7.4 13 8 13H16C16.6 13 17 12.6 17 12ZM17 7C17 6.4 16.6 6 16 6H8C7.4 6 7 6.4 7 7C7 7.6 7.4 8 8 8H16C16.6 8 17 7.6 17 7Z" fill="black"/>
                                    <path d="M15 8H20L14 2V7C14 7.6 14.4 8 15 8Z" fill="black"/>
                                  </svg>
                                </span>
                              </Link>
                              <Dropdown>
                                <Dropdown.Toggle variant="default btn-sm btn-icon btn-light-primary ms-2" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-placement="left" data-bs-original-title={lang.labels.options}>
                                  <span className="svg-icon svg-icon-3">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <rect opacity="0.3" x="2" y="2" width="20" height="20" rx="4" fill="black"/>
                                      <rect x="11" y="11" width="2.6" height="2.6" rx="1.3" fill="black"/>
                                      <rect x="15" y="11" width="2.6" height="2.6" rx="1.3" fill="black"/>
                                      <rect x="7" y="11" width="2.6" height="2.6" rx="1.3" fill="black"/>
                                    </svg>
                                  </span>
                                </Dropdown.Toggle>
                                <Dropdown.Menu variant="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 menu-state-bg-light-primary p-3 fs-7" align="end">
                                  <Dropdown.Item bsPrefix="menu-item" onClick={() => submitContactUpdate(item.id)}>
                                    <p className="menu-link fw-bold px-3 py-1 m-0">{lang.labels.edit}</p>
                                  </Dropdown.Item>
                                  <Dropdown.Item bsPrefix="menu-item" onClick={() => submitContactDelete(item.id)}>
                                    <p className="menu-link fw-bold px-3 py-1 m-0">{lang.labels.delete}</p>
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </div>
                          </td>
                        </tr>
                      )}))}
                    </tbody>
                    :
                    <tbody>
                      <tr className="text-secondary fw-bold">
                        <td colSpan={7} className="text-center">{lang.labels.noRecordsFound}</td>
                      </tr>
                    </tbody>
                  }
                </table>
              </div>
              <AppPagination showOption={false} pagination={pagination} loadPagination={loadPagination}></AppPagination>
            </div>
          </div>
        </div>
        :
        <AppPreloader></AppPreloader>
      }
      <AppModalContactExport companyForUser={companyForUser} contacts={allContacts}></AppModalContactExport>
      <AppModalContactUpdate companyForUser={companyForUser} contact={contact} contacts={contacts} contactCompanies={contactCompanies} setContact={setContact} setContacts={setContacts} loadAllContacts={loadAllContacts} loadContactsForCompany={loadContactsForCompany}></AppModalContactUpdate>
    </div>
  )
};

export default ContactListPage;
